import { useState, useCallback } from 'react'
import { Archive02, Camera, Loader2, Settings, Trash2, Undo2 } from '@/ui/icons'
import {
  Button,
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/components'
import { useConfirm } from '@/shared/hooks'

export interface TripSettingsDropdownProps {
  /** ISO timestamp when archived, or null if active */
  archivedAt: string | null
  onArchive: () => Promise<void>
  onUnarchive: () => Promise<void>
  onChangeCover: () => void
  onDelete: () => Promise<void>
}

export function TripSettingsDropdown({
  archivedAt,
  onArchive,
  onUnarchive,
  onChangeCover,
  onDelete,
}: TripSettingsDropdownProps) {
  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  const [isRestoring, setIsRestoring] = useState(false)

  const handleArchive = useCallback(async () => {
    await confirm(
      {
        title: 'Archive trip?',
        description:
          'Archived trips stay in your list under Completed / Archived. You can restore a trip anytime from trip settings.',
        variant: 'destructive',
        confirmText: 'Archive trip',
      },
      async () => {
        setConfirmLoading(true)
        try {
          await onArchive()
        } finally {
          setConfirmLoading(false)
        }
      }
    )
  }, [confirm, onArchive, setConfirmLoading])

  const handleRestore = useCallback(async () => {
    setIsRestoring(true)
    try {
      await onUnarchive()
    } finally {
      setIsRestoring(false)
    }
  }, [onUnarchive])

  const handleDelete = useCallback(async () => {
    await confirm(
      {
        title: 'Delete trip?',
        description: 'Are you sure you want to delete this trip? This action cannot be undone.',
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setConfirmLoading(true)
        try {
          await onDelete()
        } finally {
          setConfirmLoading(false)
        }
      }
    )
  }, [confirm, onDelete, setConfirmLoading])

  const isArchived = Boolean(archivedAt)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5" aria-label="Trip settings" type="button">
            <Settings className="h-4 w-4" />
            Settings
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Trip settings
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onChangeCover()}>
            <Camera className="h-4 w-4 mr-2 shrink-0" aria-hidden />
            Change cover
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {!isArchived ? (
            <DropdownMenuItem onSelect={() => void handleArchive()}>
              <Archive02 className="h-4 w-4 mr-2 shrink-0" aria-hidden />
              Archive trip
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled={isRestoring} onSelect={() => void handleRestore()}>
              {isRestoring ? (
                <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" aria-hidden />
              ) : (
                <Undo2 className="h-4 w-4 mr-2 shrink-0" aria-hidden />
              )}
              {isRestoring ? 'Restoring…' : 'Restore trip'}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:bg-destructive/10 focus:text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive"
            onSelect={() => void handleDelete()}
          >
            <Trash2 className="h-4 w-4 mr-2 shrink-0" aria-hidden />
            Delete trip
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </>
  )
}
